import React, { useEffect, useState } from 'react';
import { Package, Flame } from 'lucide-react';
import axios from 'axios';
import { shippingCategoryFor, domesticRateCard, domesticTierFee, heatpackFeeFor, HEATPACK_LABEL } from '../../lib/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fetched once per page load; the drawer opens/closes many times.
let settingsPromise = null;
export const loadSettings = () => {
  if (!settingsPromise) {
    settingsPromise = axios.get(`${API}/settings`).then((r) => r.data).catch(() => null);
  }
  return settingsPromise;
};

// Tells shoppers which shipping rate card their basket qualifies for BEFORE checkout:
// food/dry goods only → cheaper standard-packaging rate; live animals → thermal box (+ HeatPack when active).
export const CartShippingTeaser = ({ items, totalPrice }) => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { loadSettings().then(setSettings); }, []);

  if (!settings || settings.shipping_enabled === false || settings.shipping_suspended) return null;

  const category = shippingCategoryFor(items);
  const isFood = category === 'standard';
  const rates = domesticRateCard(settings, category);
  const fee = domesticTierFee(settings, totalPrice, category);
  const heatpack = heatpackFeeFor(settings, category, fee);
  const freeOver200 = rates['200_plus'] <= 0;
  const fmt = (v) => `€${(parseFloat(v) || 0).toFixed(2)}`;
  // Nearest tier above the basket that is actually cheaper than what they'd pay now.
  const nudge = fee > 0
    ? [[100, rates['100_to_200']], [200, rates['200_plus']]]
        .map(([threshold, rate]) => ({ threshold, rate, gap: threshold - totalPrice }))
        .find((t) => t.gap > 0 && t.rate < fee)
    : null;
  const progress = nudge ? Math.min(100, Math.max(4, (totalPrice / nudge.threshold) * 100)) : 0;

  return (
    <div
      className={`text-xs p-2.5 rounded-lg border ${isFood ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}
      data-testid={`cart-shipping-teaser-${category}`}
    >
      <p className="font-semibold flex items-center gap-1.5">
        <Package className="w-3.5 h-3.5 shrink-0" />
        {isFood ? 'Food & dry goods only — standard-packaging rate' : 'Live animals — insulated thermal box rate'}
      </p>
      <p className="mt-1">
        Shipping for this basket: <strong data-testid="cart-shipping-estimate">{fee > 0 ? fmt(fee) : 'FREE'}</strong>
        {!nudge && freeOver200 && totalPrice < 200 && <> · FREE on orders €200+</>}
        {' '}· collection in Cork always free
      </p>
      {nudge && (
        <div className="mt-1.5" data-testid="cart-tier-nudge">
          <p className="font-medium">
            Add <strong data-testid="cart-tier-nudge-gap">{fmt(nudge.gap)}</strong> more for{' '}
            {nudge.rate > 0 ? <>{fmt(nudge.rate)} shipping</> : <span className="text-green-700 font-bold">FREE shipping</span>}
            {' '}(orders {nudge.threshold === 200 ? '€200+' : '€100+'})
          </p>
          <div className="mt-1 h-1.5 w-full rounded-full bg-white/70 overflow-hidden" aria-hidden="true">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ${isFood ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      {isFood && (
        <p className="mt-0.5 opacity-80">No live animals, so no thermal box needed — you pay our cheaper rate.</p>
      )}
      {heatpack > 0 && (
        <p className="mt-0.5 flex items-center gap-1 text-amber-700" data-testid="cart-heatpack-teaser">
          <Flame className="w-3.5 h-3.5 shrink-0" /> +{fmt(heatpack)} {HEATPACK_LABEL} (cold-weather protection) is added at checkout
        </p>
      )}
    </div>
  );
};

export default CartShippingTeaser;
